import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Send,
  Loader2,
  Check,
  ArrowRight,
  Phone,
  Mail,
  MessageSquare,
  Users,
  Truck,
  TrendingUp,
  Wine,
} from "lucide-react";
import { toast } from "sonner";
import { submitB2BRequest } from "../lib/api";
import SEO from "../components/SEO";

const B2B_HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/91b039dc-51e4-4e53-aaae-68155d45e4bb/images/910d0d7253810e8e6c58df37f157b7ca2c16b06e4d3e00d7ff386ea262565d07.png";

const PRODUCT_INTERESTS = [
  "Red Rush (10%)",
  "Electric Energy (12%)",
  "Dark Drop (14,5%)",
  "Proefbox",
  "Combobox",
  "Merchandise",
];

const BUSINESS_TYPES = [
  "Horeca — bar/café",
  "Horeca — restaurant",
  "Horeca — club/nightlife",
  "Evenement / festival",
  "Retail / slijterij",
  "Groothandel",
  "Anders",
];

const Zakelijk = () => {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    kvk: "",
    vat_number: "",
    business_type: "",
    city: "",
    address: "",
    estimated_volume: "",
    message: "",
  });
  const [interests, setInterests] = useState([]);
  const [termsOk, setTermsOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInterest = (val) => {
    setInterests((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!termsOk) {
      toast.error("Ga akkoord met de voorwaarden.");
      return;
    }
    setLoading(true);
    try {
      await submitB2BRequest({
        ...form,
        interested_products: interests,
        terms_accepted: termsOk,
      });
      setDone(true);
      toast.success("Aanvraag verstuurd!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aanvraag versturen mislukt.";
      toast.error(typeof msg === "string" ? msg : "Aanvraag versturen mislukt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="zakelijk-page" className="relative">
      <SEO
        title="Zakelijk · Shots voor jouw zaak"
        description="Nederlandse premium shots voor horeca, events en groothandel. Hoge marge, snelle levering vanuit Assen, voorstel op maat binnen 1 werkdag."
        path="/zakelijk"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${B2B_HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.75) 60%, rgba(5,5,5,1) 100%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-24 pb-24 sm:pt-32 sm:pb-32">
          <div className="flex items-center gap-3 rise">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              ZAKELIJK · B2B / HORECA
            </span>
          </div>
          <h1
            data-testid="zakelijk-hero-title"
            className="font-display text-6xl sm:text-8xl md:text-9xl mt-6 leading-[0.85] rise-2"
          >
            TIK HEM AAN,
            <br />
            <span className="neon-red-text">OP DE ZAAK.</span>
          </h1>
          <p
            data-testid="zakelijk-hero-tagline"
            className="mt-8 text-[var(--tha-muted)] max-w-2xl text-base sm:text-lg leading-relaxed rise-3"
          >
            Bar, evenement, restaurant of pop-up? Onze shots zijn gemaakt om door
            te tikken. Vraag een prijsopgave aan — we tikken je binnen 1 werkdag
            terug met een voorstel op maat.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3 rise-4">
            <a
              href="#aanvraag"
              data-testid="zakelijk-scroll-cta"
              className="btn-red px-7 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
            >
              VRAAG AAN <ArrowRight size={16} />
            </a>
            <Link
              to="/verhaal"
              className="btn-ghost-red px-7 py-4 font-display tracking-wider text-base"
            >
              ONS VERHAAL
            </Link>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="relative py-24 border-t border-[var(--tha-border)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              WAT WIJ DOEN
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-6xl mt-6 max-w-3xl leading-none">
            SHOTS DIE VAN <span className="neon-red-text">DE PLANK VLIEGEN.</span>
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--tha-border)]">
            {[
              {
                icon: TrendingUp,
                title: "HOGE MARGE",
                desc: "Klein formaat, snelle omloop. Ideaal voor bar, festival of impuls-verkoop.",
              },
              {
                icon: Wine,
                title: "3 SMAKEN + BOXEN",
                desc: "Red Rush, Electric Energy, Dark Drop. Ook proef- en combo-dozen als hero-SKU.",
              },
              {
                icon: Truck,
                title: "SNELLE LEVERING",
                desc: "Vanuit Assen (NL). Standaard binnen 3-5 werkdagen op locatie, sneller op afspraak.",
              },
            ].map((f, i) => (
              <div key={i} className="bg-[#070707] p-8">
                <f.icon size={28} className="text-[var(--tha-red)]" />
                <div className="font-display text-2xl mt-5 tracking-wide">{f.title}</div>
                <p className="mt-3 text-sm text-[var(--tha-muted)] leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AANVRAAG FORM */}
      <section
        id="aanvraag"
        data-testid="zakelijk-form-section"
        className="relative py-24 border-t border-[var(--tha-border)] bg-[#080808]"
      >
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              STAP 1 · JOUW GEGEVENS
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-6xl mt-6 leading-none">
            VRAAG JE <span className="neon-red-text">OFFERTE AAN.</span>
          </h2>
          <p className="mt-5 text-[var(--tha-muted)] max-w-xl">
            Vul het formulier in — geen verplichtingen, we bellen of mailen je
            binnen 1 werkdag met een voorstel op maat.
          </p>

          {done ? (
            <div data-testid="b2b-success" className="mt-12 border border-[var(--tha-red)] bg-[#1a0303] p-10 text-center">
              <div className="inline-flex w-16 h-16 items-center justify-center border-2 border-[var(--tha-red)] text-[var(--tha-red)]">
                <Check size={30} />
              </div>
              <div className="font-display text-4xl sm:text-5xl mt-6 leading-none">
                TIK, <span className="neon-red-text">BINNEN.</span>
              </div>
              <p className="mt-4 text-[var(--tha-muted)] max-w-md mx-auto">
                Je aanvraag is ontvangen. We tikken je{" "}
                <strong className="text-white">binnen 1 werkdag</strong> aan met een
                voorstel op maat.
              </p>
              <Link
                to="/"
                className="mt-8 btn-ghost-red inline-flex items-center gap-2 px-6 py-3 font-display tracking-wider"
              >
                TERUG NAAR HOME <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 space-y-8">
              {/* Company */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field icon={Building2} label="Bedrijfsnaam" required>
                  <input
                    data-testid="b2b-company-name"
                    type="text"
                    required
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="Bijv. Café De Tik"
                  />
                </Field>
                <Field icon={Users} label="Contactpersoon" required>
                  <input
                    data-testid="b2b-contact-name"
                    type="text"
                    required
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="Jouw naam"
                  />
                </Field>
                <Field icon={Mail} label="E-mail" required>
                  <input
                    data-testid="b2b-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="inkoop@bedrijf.nl"
                  />
                </Field>
                <Field icon={Phone} label="Telefoon" required>
                  <input
                    data-testid="b2b-phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="06 12 34 56 78"
                  />
                </Field>
                <Field label="KvK-nummer">
                  <input
                    data-testid="b2b-kvk"
                    type="text"
                    value={form.kvk}
                    onChange={(e) => setForm({ ...form, kvk: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="12345678"
                  />
                </Field>
                <Field label="BTW-nummer">
                  <input
                    data-testid="b2b-vat"
                    type="text"
                    value={form.vat_number}
                    onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="NL001234567B01"
                  />
                </Field>
                <Field label="Type onderneming">
                  <select
                    data-testid="b2b-business-type"
                    value={form.business_type}
                    onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white"
                  >
                    <option value="" className="bg-[#0a0a0a]">
                      — Kies —
                    </option>
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#0a0a0a]">
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Plaats">
                  <input
                    data-testid="b2b-city"
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                    placeholder="Assen"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Adres (straat + huisnummer)">
                    <input
                      data-testid="b2b-address"
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                      placeholder="Voorbeeldstraat 12"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Geschat volume (bijv. 500 shots per maand)">
                    <input
                      data-testid="b2b-volume"
                      type="text"
                      value={form.estimated_volume}
                      onChange={(e) => setForm({ ...form, estimated_volume: e.target.value })}
                      className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                      placeholder="Bijv. 500 shots per maand"
                    />
                  </Field>
                </div>
              </div>

              {/* Interests */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
                    STAP 2 · WAAR HEB JE INTERESSE IN?
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PRODUCT_INTERESTS.map((p) => {
                    const active = interests.includes(p);
                    return (
                      <button
                        type="button"
                        key={p}
                        data-testid={`b2b-interest-${p.split(" ")[0].toLowerCase()}`}
                        onClick={() => toggleInterest(p)}
                        className={`text-left px-4 py-3 border text-xs font-mono-x tracking-wider transition-colors ${
                          active
                            ? "border-[var(--tha-red)] bg-[#1a0303] text-white"
                            : "border-[var(--tha-border)] bg-[#050505] text-[var(--tha-muted)] hover:text-white hover:border-white"
                        }`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={`inline-block w-3 h-3 border ${
                              active
                                ? "bg-[var(--tha-red)] border-[var(--tha-red)]"
                                : "border-[var(--tha-muted)]"
                            }`}
                          />
                          {p}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
                    STAP 3 · TOELICHTING
                  </span>
                </div>
                <Field icon={MessageSquare} label="Vertel ons meer">
                  <textarea
                    data-testid="b2b-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)] resize-none"
                    placeholder="Wat voor bedrijf, wat is de gelegenheid, wanneer wil je starten, speciale wensen..."
                  />
                </Field>
              </div>

              {/* Terms */}
              <div className="border-t border-[var(--tha-border)] pt-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="b2b-terms"
                    type="checkbox"
                    checked={termsOk}
                    onChange={(e) => setTermsOk(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[var(--tha-red)]"
                  />
                  <span className="text-sm text-[var(--tha-muted)]">
                    Ik ga akkoord met de{" "}
                    <Link to="/voorwaarden" target="_blank" className="text-white underline">
                      algemene voorwaarden
                    </Link>{" "}
                    en het{" "}
                    <Link to="/privacy" target="_blank" className="text-white underline">
                      privacybeleid
                    </Link>
                    .
                  </span>
                </label>
              </div>

              <button
                data-testid="b2b-submit"
                type="submit"
                disabled={loading}
                className="w-full py-5 font-display tracking-wider text-lg btn-red inline-flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "VERZENDEN..." : "VERSTUUR ZAKELIJKE AANVRAAG"}
              </button>
              <p className="text-center text-xs text-[var(--tha-muted)] font-mono-x">
                We reageren binnen 1 werkdag · Geen verplichtingen · Info blijft
                intern.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ icon: Icon, label, required, children }) => (
  <label className="flex flex-col gap-1.5 p-3 border border-[var(--tha-border)] bg-[#050505] focus-within:border-white transition-colors">
    <span className="text-[10px] font-mono-x tracking-[0.25em] text-[var(--tha-muted)] inline-flex items-center gap-2">
      {Icon && <Icon size={11} />} {label}
      {required && <span className="text-[var(--tha-red)]">*</span>}
    </span>
    {children}
  </label>
);

export default Zakelijk;
